import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Box, Button, Card, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import {
  deleteProcedureDetailByIdWoLoader,
  getPetsCompliantSummaryWoLoader,
  updateProcedureByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../../util/AppColors";
import AddConsumablesDrawer from "./AddConsumablesDrawer";
import ConsumableCard from "./ConsumableCard";
import EditConsumableDrawer from "./EditConsumableDrawer";

const ConsumablesSection = () => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedConsumable, setSelectedConsumable] = useState(null);
  const [delModVsble, setDelModVsble] = useState(false);
  const [deleteDetailId, setDeleteDetailId] = useState(null);
  const [togglingDetailId, setTogglingDetailId] = useState(null);
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;

  const handleOpenAddDrawer = () => {
    setIsAddDrawerOpen(true);
  };

  const handleOpenEditDrawer = (item) => {
    setSelectedConsumable(item);
    setIsEditDrawerOpen(true);
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
    setDeleteDetailId(null);
  };

  const handleDeleteConsumable = (detailId) => {
    if (!detailId) return;
    setDeleteDetailId(detailId);
    setDelModVsble(true);
  };

  const onConfirmDelete = () => {
    if (!deleteDetailId) return;
    dispatch(deleteProcedureDetailByIdWoLoader(deleteDetailId)).then((res) => {
      if (res?.payload) {
        onCloseDelMod();
        if (appDet?.appointmentId) {
          dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
        }
      }
    });
  };

  const handleToggleUsed = (consumable) => {
    const reqProced = consumable?.procedure;
    if (!consumable || !reqProced?.procedureId || togglingDetailId) return;

    const { procedure, ...detail } = consumable;
    const reqObj = {
      ...detail,
      status: true,
      remarks: "Requested",
      isDelete: false,
      patientWorkbook: true,
    };

    setTogglingDetailId(consumable?.detailId ?? consumable?.id ?? true);
    dispatch(
      updateProcedureByIdWoLoader({
        id: reqProced?.procedureId,
        data: {
          serviceCharge: reqProced?.serviceCharge,
          serviceType: reqProced?.serviceType,
          appointmentId: reqDet?.appointment?.appointmentId,
          procedureDetails: [reqObj],
          tax: Number(reqProced?.tax ?? 0),
          total: Number(reqProced?.total ?? 0),
          mrpPrice: Number(reqProced?.mrpPrice ?? 0),
          procedureVariantId: reqProced?.procedureVariantId,
          quantity: Number(reqProced?.quantity ?? 1),
          hsnSacCode: reqProced?.hsnSacCode ?? null,
          paymentId: reqDet?.paymentId ?? null,
        },
      }),
    ).then((res) => {
      setTogglingDetailId(null);
      if (res?.payload && appDet?.appointmentId) {
        dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
      }
    });
  };

  const getConsumableList = () => {
    const reqList = reqDet?.procedures ?? [];
    return reqList
      ?.flatMap((procedure) =>
        (procedure?.procedureDetails ?? [])
          ?.filter((detail) => detail?.type === "Consumables")
          ?.map((detail) => ({ ...detail, procedure })),
      )
      ?.sort((a, b) => a.detailId - b.detailId);
  };

  return (
    <Box sx={{ width: "100%", mt: 2, mb: 4 }}>
      <Card
        sx={{
          borderRadius: "8px",
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e0e0e0",
          backgroundColor: "#ffffff",
          overflow: "hidden",
        }}
      >
        {/* Accordion / Header Bar */}
        <Box
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.8,
            cursor: "pointer",
            backgroundColor: "#ffffff",
            userSelect: "none",
            "&:hover": {
              backgroundColor: "#fafafa",
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: AppColors.appPrimary,
              fontSize: "16px",
            }}
          >
            Consumables ({getConsumableList()?.length})
          </Typography>

          <IconButton size="small" sx={{ color: AppColors.appPrimary }}>
            {isExpanded ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
          </IconButton>
        </Box>

        {/* Section Content */}
        {isExpanded && (
          <Box px={2.5} pb={2.5} pt={1}>
            {/* Consumables Card List */}
            <Box mb={2}>
              {getConsumableList()?.map((item) => (
                <ConsumableCard
                  key={item.detailId}
                  consumable={item}
                  onEdit={handleOpenEditDrawer}
                  onDelete={handleDeleteConsumable}
                  onToggleUsed={handleToggleUsed}
                  isToggling={
                    togglingDetailId === (item?.detailId ?? item?.id)
                  }
                />
              ))}
              {getConsumableList()?.length === 0 && (
                <Typography sx={{ fontSize: "14px", color: "#9e9e9e", py: 2 }}>
                  No consumables added yet.
                </Typography>
              )}
            </Box>

            {/* Dashed Add Item Button */}
            <Box
              onClick={handleOpenAddDrawer}
              sx={{
                border: "1.5px dashed #90caf9",
                borderRadius: "8px",
                backgroundColor: "#f8faff",
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "#eef4ff",
                  borderColor: AppColors.appPrimary,
                },
              }}
            >
              <Button
                startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                sx={{
                  color: AppColors.appPrimary,
                  fontWeight: 700,
                  fontSize: "14px",
                  textTransform: "none",
                  pointerEvents: "none",
                }}
              >
                Add item
              </Button>
            </Box>
          </Box>
        )}
      </Card>

      {/* Drawers */}
      <AddConsumablesDrawer
        open={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
      />

      <EditConsumableDrawer
        open={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        consumable={selectedConsumable}
      />

      <CustomModal
        open={delModVsble}
        onClose={onCloseDelMod}
        header=""
        modal
        modalWidth={30}
        headerShown={false}
      >
        <div className="flex-center">
          <Typography className="txt-semi-bold fs16 text-align-center mv20 orange4">
            Are you sure you want to delete this record?
          </Typography>
        </div>
        <div className="flex-row-ali-cen flex-center pb20">
          <div>
            <CustomButton
              text="Yes"
              redBtn
              smallBtn
              onClick={onConfirmDelete}
            />
          </div>
          <div className="ml20">
            <CustomButton text="No" smallBtn onClick={onCloseDelMod} />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
};

export default ConsumablesSection;
